<script lang="ts" setup>
import { classMerge } from '@toni/brand-ui'
import { usePageStore } from '@toni/rendering/src/stores/page'
import { useConfigStore } from '@toni/rendering/src/stores/config'
import { Avatar as AvatarRoot, AvatarImage, AvatarFallback, type AvatarProps } from '@toni/brand-ui'

import { createImgSrc, getImgDefinitions } from '../../bauer-image/image-utils'

import type { Image } from '../../../utils/content-block-schema/image-schema'

const brand = usePageStore().brandFromStore as string
const imageServiceUrl = useConfigStore().rsConfig.imageServiceUrl as string
const imageSrcDefinitions = useConfigStore().rsConfig.imageSrcDefinitions
const setName = 'avatar' as string
const imageDefinitions = getImgDefinitions(brand, imageSrcDefinitions, setName)
const imgFallbackWidth = imageDefinitions.maxWidth ?? imageSrcDefinitions?.maxWidth ?? 0

const props = withDefaults(
  defineProps<{
    image?: Image,
    size?: AvatarProps['size'],
    id?: number,
    name: string
  }>(),
  {
    image: undefined,
    size: 'xl',
    id: 0
  }
)

const imgSrc = () => {
  if (!props.image) return undefined

  return createImgSrc({
    image: props.image,
    brand,
    imageServiceUrl
  }, imgFallbackWidth)?.href ?? ''
}

function positionStyles (): string {
  const leftClasses: { [key: number]: string } = {
    1: '-left-2.5',
    2: '-left-5',
    3: '-left-7'
  }
  return props.id > 0 ? `relative ${leftClasses[props.id]}` : ''
}

const avatarOuterStyles = positionStyles()

function getInitials (name: string, maxInitials: number = 2): string {
  if (!name) return ''

  return name
    .split(' ')
    .map(part => part[0])
    .filter(char => char)
    .slice(0, maxInitials)
    .join('')
    .toUpperCase()
}
</script>

<template>
  <AvatarRoot :size="size"
              :class="classMerge(avatarOuterStyles)">
    <AvatarImage v-if="imgSrc()"
                 :img-src="imgSrc()"
                 :alt="image?.imageAltText ?? ''" />
    <AvatarFallback v-else
                    :size="size">
      {{ getInitials(name) }}
    </AvatarFallback>
  </AvatarRoot>
</template>
